import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { getIdToken } from '../attendedUtils';

const API = config.API_URL;
const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB
const ENTITY_TYPES = ['artist', 'event', 'festival', 'member', 'promoter', 'venue'];
const EVENT_FILTER_TYPES = ['artist', 'festival', 'promoter', 'venue'];

// ── Entity endpoint map ───────────────────────────────────────────────────────
const ENTITY_CONFIG = {
    artist:   { endpoint: '/artists',   getLabel: r => r.name, getSub: r => [r.city, r.state].filter(Boolean).join(', ') },
    event:    { endpoint: '/events',    getLabel: r => r.headliner || '(no headliner)', getSub: r => r.event_date ? new Date(r.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '' },
    venue:    { endpoint: '/venues',    getLabel: r => r.name, getSub: r => [r.city, r.state].filter(Boolean).join(', ') },
    festival: { endpoint: '/festivals', getLabel: r => r.name, getSub: () => '', extractData: d => d.festivals || d },
    promoter: { endpoint: '/promoters', getLabel: r => r.name, getSub: r => [r.city, r.state].filter(Boolean).join(', '), extractData: d => d.promoters || d },
    member:   { endpoint: '/members',   getLabel: r => r.name, getSub: r => [r.hometown_city, r.hometown_state].filter(Boolean).join(', '), extractData: d => d.members || d },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function entityLabel(type) {
    if (!type) return '—';
    return type.charAt(0).toUpperCase() + type.slice(1);
}

// ── Entity Search (typeahead) ─────────────────────────────────────────────────
function EntitySearch({ entityType, value, onChange }) {
    const [query, setQuery] = useState(value?.name || '');
    const [records, setRecords] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const cfg = ENTITY_CONFIG[entityType];

    // Load records when entityType changes
    useEffect(() => {
        if (!entityType || !cfg) return;
        setQuery('');
        setRecords([]);
        setLoaded(false);
        onChange(null);
        axios.get(`${API}${cfg.endpoint}`).then(res => {
            const data = cfg.extractData ? cfg.extractData(res.data) : res.data;
            setRecords(Array.isArray(data) ? data : []);
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close on outside click
    useEffect(() => {
        function onOut(e) { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const q = query.trim().toLowerCase();
    const filtered = q && loaded
        ? records.filter(r => cfg.getLabel(r).toLowerCase().includes(q) || cfg.getSub(r).toLowerCase().includes(q)).slice(0, 8)
        : [];

    const handleSelect = (r) => {
        setQuery(cfg.getLabel(r));
        onChange({ id: r.id, name: cfg.getLabel(r) });
        setOpen(false);
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <input
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); setOpen(true); onChange(null); }}
                onFocus={() => setOpen(true)}
                placeholder={loaded ? `Search ${entityLabel(entityType)}s…` : 'Loading…'}
                style={S.input}
            />
            {open && filtered.length > 0 && (
                <div style={S.searchDropdown}>
                    {filtered.map(r => (
                        <div
                            key={r.id}
                            onMouseDown={() => handleSelect(r)}
                            style={S.searchRow}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2a2040'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <div style={{ fontSize: '13px', color: '#e0e0e0' }}>{cfg.getLabel(r)}</div>
                            {cfg.getSub(r) && <div style={{ fontSize: '11px', color: '#777' }}>{cfg.getSub(r)}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Event Picker ──────────────────────────────────────────────────────────────
function EventPicker({ value, onChange }) {
    const [filterType, setFilterType] = useState('artist');
    const [filterSearch, setFilterSearch] = useState(null); // { id, name }
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsError, setEventsError] = useState('');
    const [refine, setRefine] = useState('');

    const formatEventDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';

    // When a filter entity is selected, load its events
    useEffect(() => {
        if (!filterSearch) { setEvents([]); return; }
        setEventsLoading(true);
        setEventsError('');
        setEvents([]);

        (async () => {
            try {
                const res = await axios.get(`${API}/${filterType === 'artist' ? 'artists' : filterType === 'venue' ? 'venues' : filterType === 'festival' ? 'festivals' : 'promoters'}/${filterSearch.id}`);
                let rows = [];
                if (filterType === 'artist' || filterType === 'venue') {
                    rows = (res.data.show_history || []).map(e => ({
                        event_id: e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city: e.city || e.venue_city,
                        state: e.state || e.venue_state,
                        country: e.country || e.venue_country,
                        artists: e.other_artists
                            ? (e.festival_name ? `${e.festival_name} - ` : '') + (filterType === 'artist' ? '' : '') + e.other_artists
                            : e.festival_name || '—',
                        headliner: filterType === 'artist' ? filterSearch.name : null,
                        raw: e,
                    }));
                } else if (filterType === 'festival') {
                    rows = (res.data.events || []).map(e => ({
                        event_id: e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city: e.city,
                        state: e.state,
                        country: e.country,
                        artists: e.artists || '—',
                        raw: e,
                    }));
                } else if (filterType === 'promoter') {
                    rows = (res.data.events || []).map(e => ({
                        event_id: e.event_id,
                        event_slug: e.event_slug,
                        event_date: e.event_date,
                        venue_name: e.venue_name,
                        city: e.venue_city,
                        state: e.venue_state,
                        country: e.venue_country,
                        artists: e.artists || '—',
                        raw: e,
                    }));
                }
                setEvents(rows);
            } catch {
                setEventsError('Failed to load events.');
            } finally {
                setEventsLoading(false);
            }
        })();
    }, [filterSearch, filterType]);

    // Refine filter
    const q = refine.trim().toLowerCase();
    const filtered = q ? events.filter(e =>
        [e.event_date ? formatEventDate(e.event_date) : '', e.venue_name, e.city, e.state, e.country, e.artists]
            .some(v => v && v.toLowerCase().includes(q))
    ) : events;

    return (
        <div>
            <label style={S.label}>Find events by</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                {EVENT_FILTER_TYPES.map(t => (
                    <button
                        key={t}
                        onClick={() => { setFilterType(t); setFilterSearch(null); setEvents([]); setRefine(''); onChange(null); }}
                        style={{ ...S.filterChip, ...(filterType === t ? S.filterChipActive : {}), padding: '4px 10px', fontSize: '12px' }}
                    >
                        {entityLabel(t)}
                    </button>
                ))}
            </div>

            <EntitySearch entityType={filterType} value={filterSearch} onChange={(v) => { setFilterSearch(v); onChange(null); }} />

            {filterSearch && (
                <div style={{ marginTop: '10px' }}>
                    <input
                        type="text"
                        value={refine}
                        onChange={e => setRefine(e.target.value)}
                        placeholder="Refine by date, venue, city, artists..."
                        style={{ ...S.input, marginBottom: '8px' }}
                    />

                    {eventsLoading && <div style={{ color: '#888', fontSize: '13px', padding: '8px 0' }}>Loading events…</div>}
                    {eventsError && <div style={{ color: '#e57373', fontSize: '13px' }}>{eventsError}</div>}

                    {!eventsLoading && filtered.length === 0 && !eventsError && (
                        <div style={{ color: '#666', fontSize: '13px', padding: '8px 0' }}>No events found.</div>
                    )}

                    {!eventsLoading && filtered.length > 0 && (
                        <div style={S.eventTable}>
                            <div style={S.eventTableHeader}>
                                <span>Date</span>
                                <span>Venue</span>
                                <span>City</span>
                                <span>State</span>
                                <span>Country</span>
                                <span>Lineup</span>
                            </div>
                            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                {filtered.map(e => {
                                    const isSelected = value?.event_id === e.event_id;
                                    return (
                                        <div
                                            key={e.event_id}
                                            onClick={() => onChange({ id: e.event_id, name: e.artists || formatEventDate(e.event_date) })}
                                            style={{ ...S.eventRow, ...(isSelected ? S.eventRowSelected : {}) }}
                                            onMouseEnter={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = '#2a2040'; }}
                                            onMouseLeave={ev => { if (!isSelected) ev.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            <span style={{ whiteSpace: 'nowrap' }}>{formatEventDate(e.event_date)}</span>
                                            <span style={{ color: '#ccc' }}>{e.venue_name || '—'}</span>
                                            <span style={{ color: '#aaa' }}>{e.city || '—'}</span>
                                            <span style={{ color: '#aaa' }}>{e.state || '—'}</span>
                                            <span style={{ color: '#aaa' }}>{e.country || '—'}</span>
                                            <span style={{ color: '#aaa', fontSize: '11px' }}>{e.artists || '—'}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {value && (
                        <div style={{ fontSize: '11px', color: '#40916c', marginTop: '6px' }}>
                            ✓ Selected: {formatEventDate(events.find(e => e.event_id === value.id)?.event_date)} — {events.find(e => e.event_id === value.id)?.venue_name}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Shared metadata fields (used in both Upload and Edit modals) ──────────────
function MetaFields({ caption, setCaption, entityType, setEntityType, entitySearch, setEntitySearch, isPublic, setIsPublic, isPreset = false }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
                <label style={S.label}>Caption (optional)</label>
                <input
                    type="text"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder="Describe this photo..."
                    style={S.input}
                    maxLength={280}
                />
            </div>

            {!isPreset && <div>
                <label style={S.label}>Assign to (optional)</label>
                <select
                    value={entityType}
                    onChange={e => { setEntityType(e.target.value); setEntitySearch(null); }}
                    style={{ ...S.input, marginBottom: '6px' }}
                >
                    <option value="">— None —</option>
                    {ENTITY_TYPES.map(t => (
                        <option key={t} value={t}>{entityLabel(t)}</option>
                    ))}
                </select>

                {entityType && entityType !== 'event' && (
                    <EntitySearch
                        entityType={entityType}
                        value={entitySearch}
                        onChange={setEntitySearch}
                    />
                )}

                {entityType === 'event' && (
                    <EventPicker
                        value={entitySearch}
                        onChange={setEntitySearch}
                    />
                )}

                {entityType && entityType !== 'event' && entitySearch && (
                    <div style={{ fontSize: '11px', color: '#40916c', marginTop: '4px' }}>
                        ✓ Assigned to {entityLabel(entityType)}: {entitySearch.name}
                    </div>
                )}
            </div>}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ ...S.label, margin: 0 }}>Visibility</label>
                <button
                    onClick={() => setIsPublic(v => !v)}
                    style={{ ...S.toggleBtn, backgroundColor: isPublic ? '#2d6a4f' : '#444', borderColor: isPublic ? '#40916c' : '#555' }}
                >
                    {isPublic ? '🌐 Public' : '🔒 Private'}
                </button>
                <span style={{ fontSize: '12px', color: '#888' }}>
                    {isPublic ? 'Visible on the entity page' : 'Only visible to you'}
                </span>
            </div>
        </div>
    );
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
function UploadModal({ onClose, onUploaded, presetEntityType, presetEntityId, presetEntityName, onAutoDefault }) {
    const isPreset = !!(presetEntityType && presetEntityId);
    const [mode, setMode] = useState('file');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [caption, setCaption] = useState('');
    const [entityType, setEntityType] = useState(presetEntityType || '');
    const [entitySearch, setEntitySearch] = useState(
        presetEntityId && presetEntityName ? { id: presetEntityId, name: presetEntityName } : null
    );
    const [isPublic, setIsPublic] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    const startCamera = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch {
            setError('Camera access denied or unavailable.');
            setMode('file');
        }
    }, []);

    const stopCamera = useCallback(() => {
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    }, []);

    useEffect(() => {
        if (mode === 'camera') startCamera();
        else stopCamera();
        return stopCamera;
    }, [mode, startCamera, stopCamera]);

    const capturePhoto = () => {
        const video = videoRef.current; const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth; canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (!blob) return;
            const f = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setFile(f); setPreview(URL.createObjectURL(f)); stopCamera(); setMode('preview');
        }, 'image/jpeg', 0.92);
    };

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_FILE_BYTES) { setError('File exceeds 4 MB limit.'); return; }
        if (!f.type.startsWith('image/')) { setError('Only image files are allowed.'); return; }
        setError(''); setFile(f); setPreview(URL.createObjectURL(f)); setMode('preview');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) handleFileSelect({ target: { files: [f] } });
    };

    const toBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(f);
    });

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true); setError('');
        try {
            const token = await getIdToken();
            const data = await toBase64(file);
            const uploadRes = await axios.post(`${API}/images/upload`, { filename: file.name, contentType: file.type, data }, { headers: { Authorization: token } });
            const { s3Key } = uploadRes.data;
            const createRes = await axios.post(`${API}/images`, {
                s3Key,
                caption: caption.trim() || null,
                entityType: entityType || null,
                entityId: entitySearch?.id || null,
                isPublic,
            }, { headers: { Authorization: token } });
            const newImage = createRes.data.image;

            // Auto-default: if assigned to an event, image is public, and caller
            // signals no default exists yet, set this image as the default.
            if (
                isPublic &&
                (entityType === 'event') &&
                entitySearch?.id &&
                typeof onAutoDefault === 'function'
            ) {
                await onAutoDefault(newImage, entitySearch.id, token);
            }

            onUploaded(newImage);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    // Two-column layout once file is chosen
    const hasFile = !!file;

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ ...S.modal, maxWidth: hasFile ? ((entityType === 'event' && !isPreset) ? '1000px' : '820px') : '480px' }}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Upload Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                {/* Mode selector — only before file chosen */}
                {!hasFile && (
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                        <button onClick={() => setMode('file')} style={{ ...S.modeBtn, ...(mode === 'file' ? S.modeBtnActive : {}) }}>📁 From Device</button>
                        <button onClick={() => setMode('camera')} style={{ ...S.modeBtn, ...(mode === 'camera' ? S.modeBtnActive : {}) }}>📷 Camera</button>
                    </div>
                )}

                {/* File drop zone */}
                {mode === 'file' && !hasFile && (
                    <div style={S.dropZone} onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => fileInputRef.current?.click()}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼️</div>
                        <div style={{ color: '#aaa', fontSize: '14px' }}>Drag & drop or click to select</div>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>JPEG, PNG, GIF, WEBP · Max 4 MB</div>
                        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleFileSelect} />
                    </div>
                )}

                {/* Camera viewfinder */}
                {mode === 'camera' && !hasFile && (
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: '300px', borderRadius: '8px', objectFit: 'cover' }} />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <button onClick={capturePhoto} style={{ ...S.primaryBtn, marginTop: '10px' }}>📸 Capture Photo</button>
                    </div>
                )}

                {/* Two-column layout after file chosen */}
                {hasFile && (
                    <div style={S.twoCol}>
                        {/* Left: image preview */}
                        <div style={S.twoColLeft}>
                            <img src={preview} alt="Preview" style={{ width: '100%', borderRadius: '8px', objectFit: 'contain', background: '#111', maxHeight: '380px' }} />
                            <button onClick={() => { setFile(null); setPreview(null); setMode('file'); }} style={{ ...S.ghostBtn, marginTop: '8px', fontSize: '12px', width: '100%' }}>
                                ← Choose different photo
                            </button>
                        </div>

                        {/* Right: fields */}
                        <div style={S.twoColRight}>
                            <MetaFields
                                caption={caption} setCaption={setCaption}
                                entityType={entityType} setEntityType={setEntityType}
                                entitySearch={entitySearch} setEntitySearch={setEntitySearch}
                                isPublic={isPublic} setIsPublic={setIsPublic}
                                isPreset={isPreset}
                            />
                            {error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
                            <button onClick={handleUpload} disabled={uploading} style={{ ...S.primaryBtn, opacity: uploading ? 0.6 : 1, marginTop: '12px', width: '100%' }}>
                                {uploading ? 'Uploading…' : '⬆️  Upload Photo'}
                            </button>
                        </div>
                    </div>
                )}

                {!hasFile && error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}
            </div>
        </div>
    );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ image, onClose, onSaved, onDeleted }) {
    const [caption, setCaption] = useState(image.caption || '');
    const [entityType, setEntityType] = useState(image.entity_type || '');
    const [entitySearch, setEntitySearch] = useState(
        image.entity_id ? { id: image.entity_id, name: image.entity_name || `${entityLabel(image.entity_type)} #${image.entity_id}` } : null
    );
    const [isPublic, setIsPublic] = useState(image.is_public !== false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const token = await getIdToken();
            const res = await axios.patch(`${API}/images/${image.id}`, {
                caption: caption.trim() || null,
                entityType: entityType || null,
                entityId: entitySearch?.id || null,
                isPublic,
            }, { headers: { Authorization: token } });
            onSaved(res.data.image);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true); setError('');
        try {
            const token = await getIdToken();
            await axios.delete(`${API}/images/${image.id}`, { headers: { Authorization: token } });
            onDeleted(image.id);
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Delete failed.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ ...S.modal, maxWidth: entityType === 'event' ? '1000px' : '820px' }}>
                <div style={S.modalHeader}>
                    <span style={{ fontSize: '18px', fontWeight: 600 }}>Edit Photo</span>
                    <button onClick={onClose} style={S.closeBtn}>✕</button>
                </div>

                <div style={S.twoCol}>
                    {/* Left: image */}
                    <div style={S.twoColLeft}>
                        <img src={image.image_url} alt={image.caption || ''} style={{ width: '100%', borderRadius: '8px', objectFit: 'contain', background: '#111', maxHeight: '380px' }} />
                    </div>

                    {/* Right: fields */}
                    <div style={S.twoColRight}>
                        <MetaFields
                            caption={caption} setCaption={setCaption}
                            entityType={entityType} setEntityType={setEntityType}
                            entitySearch={entitySearch} setEntitySearch={setEntitySearch}
                            isPublic={isPublic} setIsPublic={setIsPublic}
                        />

                        {error && <div style={{ ...S.errorMsg, marginTop: '8px' }}>{error}</div>}

                        <button onClick={handleSave} disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.6 : 1, marginTop: '12px', width: '100%' }}>
                            {saving ? 'Saving…' : '💾 Save Changes'}
                        </button>

                        <div style={{ borderTop: '1px solid #333', paddingTop: '12px', marginTop: '4px' }}>
                            {!confirmDelete ? (
                                <button onClick={() => setConfirmDelete(true)} style={S.deleteBtn}>🗑️ Delete Photo</button>
                            ) : (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', color: '#e57373' }}>Delete permanently?</span>
                                    <button onClick={handleDelete} disabled={deleting} style={{ ...S.deleteBtn, opacity: deleting ? 0.6 : 1 }}>
                                        {deleting ? 'Deleting…' : 'Yes, Delete'}
                                    </button>
                                    <button onClick={() => setConfirmDelete(false)} style={S.ghostBtn}>Cancel</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyImages() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUpload, setShowUpload] = useState(false);
    const [editImage, setEditImage] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [lightboxImg, setLightboxImg] = useState(null);

    const loadImages = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const token = await getIdToken();
            const res = await axios.get(`${API}/images/mine`, { headers: { Authorization: token } });
            setImages(res.data.images || []);
        } catch {
            setError('Failed to load images.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadImages(); }, [loadImages]);

    const handleUploaded = (img) => setImages(prev => [img, ...prev]);
    const handleSaved = (img) => setImages(prev => prev.map(i => i.id === img.id ? { ...img, image_url: i.image_url } : i));
    const handleDeleted = (id) => setImages(prev => prev.filter(i => i.id !== id));

    const filtered = images.filter(img => {
        if (filterType === 'all') return true;
        if (filterType === 'unassigned') return !img.entity_type;
        return img.entity_type === filterType;
    });

    const counts = {
        all: images.length,
        unassigned: images.filter(i => !i.entity_type).length,
        ...Object.fromEntries(ENTITY_TYPES.map(t => [t, images.filter(i => i.entity_type === t).length])),
    };

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.pageHeader}>
                <h1 style={S.pageTitle}>My Images</h1>
                <button onClick={() => setShowUpload(true)} style={S.primaryBtn}>+ Upload Photo</button>
            </div>

            {/* Filter bar */}
            <div style={S.filterBar}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'unassigned', label: 'Unassigned' },
                    ...ENTITY_TYPES.map(t => ({ key: t, label: entityLabel(t) + 's' })),
                ].map(({ key, label }) => (
                    counts[key] > 0 || key === 'all' ? (
                        <button key={key} onClick={() => setFilterType(key)} style={{ ...S.filterChip, ...(filterType === key ? S.filterChipActive : {}) }}>
                            {label}<span style={S.chipCount}>{counts[key] || 0}</span>
                        </button>
                    ) : null
                ))}
            </div>

            {/* Content */}
            {loading && <div style={S.empty}>Loading…</div>}
            {!loading && error && <div style={S.errorMsg}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
                <div style={S.empty}>
                    {filterType === 'all' ? 'No photos yet. Click "Upload Photo" to get started!' : `No ${filterType === 'unassigned' ? 'unassigned' : filterType} photos.`}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div style={S.grid}>
                    {filtered.map(img => (
                        <div key={img.id} style={S.card}>
                            <div style={S.thumb} onClick={() => setLightboxImg(img)} title="View full size">
                                <img src={img.image_url} alt={img.caption || ''} style={S.thumbImg} loading="lazy" />
                                {!img.is_public && <span style={S.privateBadge}>🔒</span>}
                            </div>
                            <div style={S.cardBody}>
                                {img.caption && <div style={S.caption} title={img.caption}>{img.caption}</div>}
                                <div style={S.cardMeta}>
                                    {img.entity_type ? (
                                        <span style={S.entityBadge}>
                                            {entityLabel(img.entity_type)}: {img.entity_name || `#${img.entity_id}`}
                                        </span>
                                    ) : (
                                        <span style={{ color: '#555', fontSize: '12px' }}>Unassigned</span>
                                    )}
                                    <span style={{ color: '#555', fontSize: '11px' }}>{formatDate(img.created_at)}</span>
                                </div>
                                <button onClick={() => setEditImage(img)} style={S.editBtn}>Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {lightboxImg && (
                <div style={{ ...S.overlay, zIndex: 3000 }} onClick={() => setLightboxImg(null)}>
                    <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
                        <img src={lightboxImg.image_url} alt={lightboxImg.caption || ''} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
                        {lightboxImg.caption && <div style={S.lightboxCaption}>{lightboxImg.caption}</div>}
                        <button onClick={() => setLightboxImg(null)} style={{ ...S.closeBtn, position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>✕</button>
                    </div>
                </div>
            )}

            {showUpload && <UploadModal
                onClose={() => setShowUpload(false)}
                onUploaded={handleUploaded}
                onAutoDefault={async (newImage, eventId, token) => {
                    // Auto-default only if the event has no existing default and no existing photos
                    try {
                        const res = await axios.get(`${API}/images`, {
                            params: { entityType: 'event', entityId: eventId },
                            headers: { Authorization: token },
                        });
                        const existing = (res.data.images || []).filter(i => i.id !== newImage.id);
                        const eventRes = await axios.get(`${API}/events/${eventId}`);
                        const hasDefault = !!eventRes.data.event?.default_image_id;
                        if (!hasDefault && existing.length === 0) {
                            await axios.post(`${API}/images/set-default`,
                                { imageId: newImage.id, entityType: 'event', entityId: eventId },
                                { headers: { Authorization: token } }
                            );
                        }
                    } catch { /* non-fatal — upload already succeeded */ }
                }}
            />}
            {editImage && <EditModal image={editImage} onClose={() => setEditImage(null)} onSaved={handleSaved} onDeleted={handleDeleted} />}
        </div>
    );
}

// Export UploadModal so ArtistDetail can use it inline
export { UploadModal };

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#e0e0e0', fontFamily: 'inherit' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { fontSize: '24px', fontWeight: 700, margin: 0, color: '#e0e0e0' },
    filterBar: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
    filterChip: { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', border: '1px solid #444', background: '#2c2c2c', color: '#aaa', fontSize: '13px', cursor: 'pointer' },
    filterChipActive: { borderColor: '#bb86fc', background: '#2a2040', color: '#bb86fc' },
    chipCount: { background: '#333', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', color: '#888' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' },
    card: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    thumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    thumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', fontSize: '14px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px' },
    cardBody: { padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
    caption: { fontSize: '13px', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    cardMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
    entityBadge: { fontSize: '11px', background: '#2a2040', color: '#bb86fc', border: '1px solid #3d3060', borderRadius: '4px', padding: '2px 6px' },
    editBtn: { marginTop: '4px', padding: '5px 10px', background: 'transparent', border: '1px solid #444', borderRadius: '5px', color: '#aaa', fontSize: '12px', cursor: 'pointer', alignSelf: 'flex-end' },
    empty: { textAlign: 'center', color: '#666', marginTop: '60px', fontSize: '15px' },
    // Modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' },
    modal: { background: '#1e1e1e', border: '1px solid #333', borderRadius: '12px', padding: '24px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer', lineHeight: 1 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },
    twoColLeft: { display: 'flex', flexDirection: 'column' },
    twoColRight: { display: 'flex', flexDirection: 'column', gap: '4px' },
    dropZone: { border: '2px dashed #444', borderRadius: '10px', padding: '36px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px' },
    label: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { width: '100%', background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#e0e0e0', padding: '8px 10px', fontSize: '14px', boxSizing: 'border-box' },
    searchDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#2c2c2c', border: '1px solid #444', borderTop: 'none', borderRadius: '0 0 6px 6px', zIndex: 400, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' },
    searchRow: { padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #333', backgroundColor: 'transparent' },
    primaryBtn: { background: '#bb86fc', color: '#1a1a1a', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
    ghostBtn: { background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    deleteBtn: { background: 'transparent', border: '1px solid #e57373', borderRadius: '6px', color: '#e57373', padding: '7px 14px', fontSize: '13px', cursor: 'pointer' },
    toggleBtn: { border: '1px solid', borderRadius: '20px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', color: '#e0e0e0' },
    modeBtn: { flex: 1, background: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', color: '#aaa', padding: '8px', fontSize: '13px', cursor: 'pointer' },
    modeBtnActive: { borderColor: '#bb86fc', color: '#bb86fc', background: '#2a2040' },
    errorMsg: { color: '#e57373', background: '#2a1a1a', border: '1px solid #6b2c2c', borderRadius: '6px', padding: '8px 12px', fontSize: '13px' },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '10px 14px', fontSize: '14px', borderRadius: '0 0 8px 8px', textAlign: 'center' },
    // Event picker
    eventTable: { border: '1px solid #333', borderRadius: '6px', overflow: 'hidden', fontSize: '12px' },
    eventTableHeader: { display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1.5fr', gap: '8px', padding: '8px 10px', background: '#252525', color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #333' },
    eventRow: { display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1.5fr', gap: '8px', padding: '8px 10px', borderBottom: '1px solid #222', cursor: 'pointer', backgroundColor: 'transparent', color: '#ccc' },
    eventRowSelected: { backgroundColor: '#2a2040', color: '#bb86fc' },
};
